<template>
  <!-- Conversa com um agente (chat interno nativo do WootDash, GET/POST /mensagens-internas). A lista de agentes, o status
       e o cartão de detalhes ficam na janela Conversas da barra superior (InternalChatPopup.vue) — a faixa de avatares
       que ficava na barra lateral direita saiu. Fork de AgentChatDrawer.vue (Chatwoot/n8n), que continua servindo
       BroadcastComposeModal.vue/useDashboardEngine.ts sem mudança. -->
  <transition name="ach-panel">
    <div v-if="panelOpen && activeAgentId != null && railMode === 'agent' && canUseAgentChat" class="ach-panel" :style="{ width: panelWidth + 'px' }">
      <div class="ach-resize-handle" @mousedown.prevent="startResize"></div>

      <div class="ach-head">
        <button class="ach-icon-btn" @click="backToAgents" title="Voltar pra lista de agentes (janela Conversas)">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div class="ach-chat-hinfo" v-if="activeAgent">
          <div class="ach-chat-hav" :style="{ background: avatarColor(activeAgent.name) }">
            <img
              v-if="activeAgent.avatarUrl"
              :src="toMediaUrl(activeAgent.avatarUrl) ?? undefined"
              :alt="activeAgent.name"
              @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
            />
            <span v-else>{{ initials(activeAgent.name) }}</span>
          </div>
          <div class="ach-chat-hname-wrap">
            <div class="ach-chat-hname">{{ activeAgent.name }}</div>
            <div class="ach-chat-hstatus">
              <span class="ach-status-dot sm" :style="{ background: statusColor(activeAgent.availability) }"></span>
              {{ statusLabel(activeAgent.availability) }}
            </div>
          </div>
        </div>
        <button class="ach-icon-btn" @click="closePanel">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="ach-messages" ref="msgListEl">
        <div v-if="loadingMessages" class="ach-empty">Carregando…</div>
        <template v-else>
          <div v-if="!activeMessages.length" class="ach-empty">
            <p>Nenhuma mensagem ainda — diga olá!</p>
          </div>
          <div
            v-for="m in activeMessages"
            :key="m.id"
            class="ach-msg-row"
            :class="m.senderId === myId ? 'out' : 'inn'"
          >
            <div class="ach-bubble">
              <span class="ach-bubble-text">{{ m.body }}</span>
              <span class="ach-bubble-ts">{{ fmtTs(m.createdAt) }}</span>
            </div>
          </div>
        </template>
      </div>

      <div v-if="sendError" class="ach-error">{{ sendError }}</div>

      <form class="ach-input-row" @submit.prevent="onSend">
        <input
          v-model="draft"
          class="ach-input"
          placeholder="Mensagem… (Enter envia)"
          :disabled="sending"
        />
        <button type="submit" class="ach-send-btn" :disabled="!draft.trim() || sending">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </form>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useAgentChatCp } from '@/composables/useAgentChatCp'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { usePermission } from '@/composables/usePermission'
import { avatarColor, initials } from '@/composables/useHelpers'
import { AVAIL_OPTIONS } from '@/composables/useAvailability'
import { toMediaUrl } from '@/utils/media'
import { openInternalChatPopup } from '@/composables/useInternalChatPopup'

const store    = useStore()
const authUser = computed(() => store.getters.authUser)
const token    = computed<string>(() => authUser.value?.wootdash_token ?? '')
const myId     = computed<string>(() => authUser.value?.wootdash_agent_id ?? '')

const {
  activeAgentId, activeAgent, activeMessages,
  panelOpen, loadingMessages, sendError,
  loadAgents, loadUnread, closePanel, send,
  startRealtime, stopRealtime,
} = useAgentChatCp()

const { mode: railMode } = useChatRailMode()
const { can } = usePermission()
const canUseAgentChat = computed(() => can('agentChat.view'))

const draft      = ref('')
const sending    = ref(false)
const msgListEl  = ref<HTMLElement | null>(null)

// Sem a faixa de avatares, o trilho da direita volta pro chat de clientes assim que esta conversa fecha (e fecha se outra
// tela puxar o trilho pro chat de clientes).
watch(panelOpen, open => { if (!open && railMode.value === 'agent') railMode.value = 'client' })
watch(railMode, mode => { if (mode !== 'agent' && panelOpen.value) closePanel() })

/** Voltar: a lista de agentes agora é a janela Conversas. */
function backToAgents() {
  closePanel()
  openInternalChatPopup()
}

function statusColor(av: string): string {
  return AVAIL_OPTIONS.find(o => o.value === av)?.color ?? '#9ca3af'
}
function statusLabel(av: string): string {
  return AVAIL_OPTIONS.find(o => o.value === av)?.label ?? 'Offline'
}

function fmtTs(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function scrollToBottom() {
  nextTick(() => {
    if (msgListEl.value) msgListEl.value.scrollTop = msgListEl.value.scrollHeight
  })
}

/* ── Resize — mesmo mecanismo do AgentChatDrawer.vue original, chave própria pra não
   colidir se algum dia os dois coexistirem (não deveriam, mas custa zero garantir) ── */
const MIN_W = 260
const MAX_W = 640
const panelWidth = ref(Math.max(MIN_W, Math.min(MAX_W, parseInt(localStorage.getItem('ach_cp_panel_w') || '320'))))

let _rx = false, _sx = 0, _sw = 0

function startResize(e: MouseEvent) {
  _rx = true; _sx = e.clientX; _sw = panelWidth.value
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}
function doResize(e: MouseEvent) {
  if (!_rx) return
  panelWidth.value = Math.max(MIN_W, Math.min(MAX_W, _sw + (_sx - e.clientX)))
}
function stopResize() {
  _rx = false
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  localStorage.setItem('ach_cp_panel_w', String(panelWidth.value))
}

async function onSend() {
  const text = draft.value
  if (!text.trim() || activeAgentId.value == null) return
  draft.value = ''
  sending.value = true
  await send(token.value, activeAgentId.value, text)
  sending.value = false
  scrollToBottom()
}

watch(activeMessages, scrollToBottom)

// Carrega agentes/não-lidas e arma o realtime assim que o token, o id do agente logado
// E a permissão (agentChat.view) estiverem disponíveis.
watch(
  () => [token.value, myId.value, canUseAgentChat.value] as const,
  ([tok, id, allowed]) => {
    if (!tok || !id || !allowed) {
      if (!allowed) stopRealtime()
      return
    }
    loadAgents(tok, id)
    loadUnread(tok)
    startRealtime(tok, id)
  },
  { immediate: true },
)
</script>

<style scoped>
.ach-status-dot.sm { border: 2px solid #fff; width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }

.ach-panel {
  position: fixed;
  top: 66px; bottom: 0;
  right: 0;
  background: #fff;
  box-shadow: -6px 0 24px rgba(0,0,0,.18);
  z-index: 188;
  display: flex;
  flex-direction: column;
}

.ach-resize-handle {
  position: absolute; left: 0; top: 0; bottom: 0; width: 5px;
  cursor: col-resize; z-index: 10;
  transition: background 0.15s;
}
.ach-resize-handle:hover { background: rgba(188,12,36,.18); }
.ach-panel-enter-active, .ach-panel-leave-active { transition: transform .2s ease, opacity .2s ease; }
.ach-panel-enter-from, .ach-panel-leave-to { transform: translateX(16px); opacity: 0; }

.ach-head {
  height: 66px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: var(--red-dark);
  color: #fff;
}
.ach-icon-btn {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.1);
  border: none; border-radius: 50%;
  color: rgba(255,255,255,.85);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.14s;
}
.ach-icon-btn:hover { background: rgba(255,255,255,.2); }

.ach-chat-hinfo { flex: 1; display: flex; align-items: center; gap: 8px; min-width: 0; }
.ach-chat-hav {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 11px; font-weight: 700;
  overflow: hidden; flex-shrink: 0;
}
.ach-chat-hav img { width: 100%; height: 100%; object-fit: cover; }
.ach-chat-hname-wrap { min-width: 0; }
.ach-chat-hname {
  font-size: 12.5px; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ach-chat-hstatus {
  display: flex; align-items: center; gap: 5px;
  font-size: 10.5px; color: rgba(255,255,255,.7);
}

.ach-empty {
  flex: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12.5px;
  text-align: center;
  padding: 24px;
}

.ach-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f4f5f7;
}

.ach-msg-row { display: flex; }
.ach-msg-row.out { justify-content: flex-end; }
.ach-msg-row.inn { justify-content: flex-start; }

.ach-bubble {
  max-width: 78%;
  padding: 7px 10px;
  border-radius: 12px;
}
.ach-msg-row.out .ach-bubble { background: var(--red); color: #fff; border-bottom-right-radius: 3px; }
.ach-msg-row.inn .ach-bubble { background: #fff; color: #1a1a1a; border-bottom-left-radius: 3px; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
.ach-bubble-text { display: block; font-size: 12.5px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; }
.ach-bubble-ts { display: block; text-align: right; font-size: 10px; margin-top: 2px; }
.ach-msg-row.inn .ach-bubble-ts { color: rgba(0,0,0,.45); }
.ach-msg-row.out .ach-bubble-ts { color: rgba(255,255,255,.65); }

.ach-error {
  padding: 6px 14px;
  font-size: 11.5px;
  color: #ef4444;
  background: rgba(239,68,68,.08);
}

.ach-input-row {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid rgba(0,0,0,.08);
  background: #fff;
}
.ach-input {
  flex: 1;
  border: 1px solid rgba(0,0,0,.12);
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 12.5px;
  font-family: inherit;
  outline: none;
  background: #fff;
  color: #1a1a1a;
}
.ach-input:focus { border-color: var(--red); }
.ach-send-btn {
  width: 32px; height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--red);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.14s;
}
.ach-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

</style>
